import { useScreenSize } from '@myosh/odin-components';
import { useCallback, useState } from 'react';
import { usePopState } from '../../../hooks/use-pop-state';
import { useAppSelector } from '../../../redux/hooks';
import { selectActiveRecordMinimised, selectIsActivePanelEmpty } from '../../../redux/slices/layout';

/**
 * Ensures `isMobile` keeps in sync with Tailwind `sm` breakpoint
 * so that the record goes to fullscreen as the fullscreen button disappears.
 */
const MOBILE_BREAKPOINT = 640;

interface UseActiveRecordLayoutReturn {
  shouldHideMainLayout: boolean;
  isMobile: boolean;
  isFullScreen: boolean;
  setIsFullScreen: (fullScreen: boolean) => void;
}

/**
 * Hook to manage active record panel layout visibility logic.
 * Determines when the main content should be hidden based on:
 * - Mobile viewport with active record open and not minimised
 * - Fullscreen mode active
 *
 * @returns Layout state and controls
 */
export const useActiveRecordLayout = (): UseActiveRecordLayoutReturn => {
  const { isMobile } = useScreenSize(MOBILE_BREAKPOINT);
  const activePanelFilled = !useAppSelector(selectIsActivePanelEmpty);
  const activeRecordMinimised = useAppSelector(selectActiveRecordMinimised);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Reset fullscreen state on browser back/forward navigation
  // This is needed because window.history.replaceState bypasses React Router,
  // causing state to persist incorrectly after back navigation
  const handlePopState = useCallback(() => {
    setIsFullScreen(() => false);
  }, []);

  usePopState(handlePopState);

  const shouldFillViewport = isMobile || isFullScreen;
  const shouldShowRecord = activePanelFilled && !activeRecordMinimised;
  const shouldHideMainLayout = shouldFillViewport && shouldShowRecord;

  return {
    shouldHideMainLayout,
    isMobile,
    isFullScreen,
    setIsFullScreen,
  };
};
